import React, { useCallback, useEffect, useState } from "react";
import Axios from "axios";
import debounce from "lodash.debounce";
import { useMutation, useQuery } from "react-query";
import { Button, Form, Select, Switch } from "antd";
import ModalSpecial from "../../../../../components/Layout/Modals/ModalSpecial";
import Treeselect from "../../../../../components/common/Treeselect";
import SelectInput from "../../../../../components/common/SelectInput";
import {
  antdAsyncMessageError,
  antdAsyncMessageSuccess,
} from "../../../../../helpers/AsyncMessage";

const SCOPE_OPTIONS = [
  { label: "Bigbox_lt", value: "Bigbox_lt" },
  { label: "Bigbox_lv", value: "Bigbox_lv" },
  { label: "Bigbox_ee", value: "Bigbox_ee" },
  { label: "Bigbox_fi", value: "Bigbox_fi" },
  { label: "Technorama_lt", value: "Technorama_lt" },
];

// Build a hierarchy tree from the flat simple_list response.
// Uses category.code as value so codes are stored directly.
const convertToTreeData = (flatData) => {
  const itemMap = {};
  flatData.forEach((item) => {
    itemMap[item.id] = {
      name: item.name,
      value: item.code,
      children: [],
    };
  });

  const roots = [];
  flatData.forEach((item) => {
    if (item.parent_id !== null && itemMap[item.parent_id]) {
      itemMap[item.parent_id].children.push(itemMap[item.id]);
    } else {
      roots.push(itemMap[item.id]);
    }
  });
  return roots;
};

const ModalNonPublishableKeyword = ({ onClose, modalOpt, onSaved }) => {
  const [form] = Form.useForm();
  const isEdit = modalOpt.type === "edit_keyword";
  const MATCH_TYPE_OPTIONS = ["or", "and"];
  const [keywordMatchType, setKeywordMatchType] = useState(undefined);
  const [keywordMatchTypeError, setKeywordMatchTypeError] = useState("");

  const [disableByScope, setDisableByScope] = useState(false);
  const [scopesValue, setScopesValue] = useState([]);
  const [scopesError, setScopesError] = useState("");
  const [ignoreIfWarehouseStock, setIgnoreIfWarehouseStock] = useState(false);

  // ---- Category tree (loaded once on first interaction) ----
  const [categoryTreeOptions, setCategoryTreeOptions] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryCodesValue, setCategoryCodesValue] = useState([]);

  const loadCategoryTree = useCallback(async () => {
    if (categoriesLoaded || categoriesLoading) return;
    setCategoriesLoading(true);
    try {
      const { data } = await Axios.post("/api/v1/categories/simple_list", {
        page: 1,
        per_page: 9999999,
      });
      setCategoryTreeOptions(convertToTreeData(data));
      setCategoriesLoaded(true);
    } catch (err) {
      console.error("Failed to load category tree", err);
    } finally {
      setCategoriesLoading(false);
    }
  }, [categoriesLoaded, categoriesLoading]);

  // ---- Brands (loaded on search) ----
  const [brandsOptions, setBrandsOptions] = useState([]);
  const [brandsValue, setBrandsValue] = useState([]);

  const fetchBrandsOptions = async (search) => {
    if (!search || search.trim().length < 1) return;
    try {
      const { data } = await Axios.get(
        `/api/v1/brands?per_page=30&q[title_i_cont_any]=${encodeURIComponent(search.trim())}`
      );
      setBrandsOptions(data.map((b) => ({ label: b.title, value: b.code })));
    } catch (err) {
      console.error("Failed to fetch brands", err);
    }
  };

  const debouncedFetchBrands = useCallback(debounce(fetchBrandsOptions, 400), []);

  // ---- Prefill when editing ----
  useQuery(
    ["nonPublishableKeywordShow", modalOpt.record_id],
    () =>
      Axios.get(`/api/v1/non_publishable_keywords/${modalOpt.record_id}`).then(
        (res) => res.data
      ),
    {
      enabled: isEdit && !!modalOpt.record_id,
      onSuccess: async (response) => {
        const data = response?.data || {};
        form.setFieldsValue({
          keywords: data.keywords || [],
          active: data.active,
          disable_by_scope: !!data.disable_by_scope,
        });
        setKeywordMatchType(data.keyword_match_type || undefined);
        setKeywordMatchTypeError("");
        setDisableByScope(!!data.disable_by_scope);
        setScopesValue(data.scopes || []);
        setScopesError("");
        setIgnoreIfWarehouseStock(!!data.ignore_if_warehouse_stock);

        if (data.category_codes?.length) {
          setCategoryCodesValue(data.category_codes);
          // Also load the tree so the selected nodes are visible
          if (!categoriesLoaded) loadCategoryTree();
        }

        if (data.brand_codes?.length) {
          try {
            const params = data.brand_codes
              .map((code) => `q[code_in][]=${encodeURIComponent(code)}`)
              .join("&");
            const { data: brandData } = await Axios.get(
              `/api/v1/brands?per_page=200&${params}`
            );
            const opts = brandData.map((b) => ({ label: b.title, value: b.code }));
            setBrandsOptions(opts);
            setBrandsValue(opts);
          } catch (err) {
            console.error("Failed to preload brands", err);
          }
        }
      },
    }
  );

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        keywords: [],
        active: false,
        disable_by_scope: false,
      });
      setKeywordMatchType(undefined);
      setKeywordMatchTypeError("");
      setCategoryCodesValue([]);
      setBrandsValue([]);
      setDisableByScope(false);
      setScopesValue([]);
      setScopesError("");
      setIgnoreIfWarehouseStock(false);
    }
  }, [form, isEdit]);

  // ---- Save ----
  const handleSubmit = (values) => {
    const matchType = keywordMatchType;

    // Defensive guard: do not allow submit unless a valid match type is selected.
    if (!MATCH_TYPE_OPTIONS.includes(matchType)) {
      setKeywordMatchTypeError("Please select keyword match type");
      return;
    }

    if (values.disable_by_scope && scopesValue.length === 0) {
      setScopesError("Please select at least one scope when disable by scope is enabled");
      return;
    }

    setKeywordMatchTypeError("");
    setScopesError("");
    saveMutation.mutate(values);
  };

  const saveMutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        non_publishable_keyword: {
          keywords: values.keywords || [],
          keyword_match_type: keywordMatchType,
          scopes: scopesValue,
          disable_by_scope: !!values.disable_by_scope,
          ignore_if_warehouse_stock: ignoreIfWarehouseStock,
          active: values.active,
          category_codes: categoryCodesValue,
          brand_codes: brandsValue.map((b) => b.value),
        },
      };
      if (isEdit) {
        return Axios.put(
          `/api/v1/non_publishable_keywords/${modalOpt.record_id}`,
          payload
        );
      }
      return Axios.post("/api/v1/non_publishable_keywords", payload);
    },
    onSuccess: () => {
      antdAsyncMessageSuccess({
        key: "non_publishable_keyword_save",
        message: isEdit ? "Successfully updated" : "Successfully created",
      });
      onSaved();
      onClose();
    },
    onError: antdAsyncMessageError,
  });

  return (
    <ModalSpecial
      style={{ width: 460 }}
      text={
        <span className="font-bold text-xl text-black">
          {isEdit ? "Edit" : "New"} Non-Publishable Keyword
        </span>
      }
      onClose={onClose}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          label="Keywords"
          name="keywords"
        >
          <Select
            mode="tags"
            placeholder="Type and press Enter to add keywords"
            tokenSeparators={[","]}
          />
        </Form.Item>

        <Form.Item
          label="Keywords Select Type"
          validateStatus={keywordMatchTypeError ? "error" : ""}
          help={keywordMatchTypeError || null}
          required={true}
        >
          <SelectInput
            options={[
              { label: "OR", value: "or" },
              { label: "AND", value: "and" },
            ]}
            placeholder="Select type"
            with_empty={false}
            required={true}
            filterByLabel
            value={keywordMatchType}
            onChange={(value) => {
              setKeywordMatchType(value);
              if (keywordMatchTypeError) setKeywordMatchTypeError("");
            }}
          />
          <small>* "OR" means the product will be disabled if any of the keywords match. "AND" means the product will be disabled only if all of the keywords match.</small>
        </Form.Item>

        <Form.Item
          label="Disable for scope"
          name="disable_by_scope"
          valuePropName="checked"
          initialValue={false}
          className="mb-0"
        >
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            onChange={(checked) => {
              setDisableByScope(checked);
              if (!checked) setScopesError("");
            }}
          />
          <div>
            <small>* If is set to "Yes", the product will be disabled only for the selected scopes, if "No" - product will be set as inactive.</small>
          </div>
        </Form.Item>

        <Form.Item
          label="Scope"
          required={disableByScope}
          validateStatus={scopesError ? "error" : ""}
          help={scopesError || null}
        >
          <Select
            mode="multiple"
            options={SCOPE_OPTIONS}
            placeholder="Select scopes"
            value={scopesValue}
            onChange={(value) => {
              setScopesValue(value || []);
              if (scopesError && value?.length > 0) setScopesError("");
            }}
          />
          <small>* If at least one locale is selected, the product will not be disabled—it will remain active. Only in the selected locale will it be deactivated.</small>
        </Form.Item>

        {/* Category tree — loads all categories once on first click/focus */}
        <Form.Item label="Categories">
          <div
            onMouseDown={loadCategoryTree}
            onFocus={loadCategoryTree}
          >
            <Treeselect
              options={categoryTreeOptions}
              value={categoryCodesValue}
              multiSelect={true}
              showStrategy="SHOW_ALL"
              placeholder={
                categoriesLoading
                  ? "Loading categories…"
                  : "Click to search categories"
              }
              onChange={(values) => setCategoryCodesValue(values || [])}
            />
          </div>
        </Form.Item>

        {/* Brands — server-side search on each keystroke */}
        <Form.Item label="Brands">
          <Select
            mode="multiple"
            labelInValue
            options={brandsOptions}
            value={brandsValue}
            filterOption={false}
            showSearch
            placeholder="Type to search brands"
            onSearch={debouncedFetchBrands}
            onChange={(value) => setBrandsValue(value)}
            style={{ width: "100%" }}
            dropdownMatchSelectWidth={false}
          />
        </Form.Item>

        <Form.Item
          label="Ignore if warehouse stock"
        >
          <Switch
            checked={ignoreIfWarehouseStock}
            checkedChildren="Yes"
            unCheckedChildren="No"
            onChange={setIgnoreIfWarehouseStock}
          />
          <br/><small>* If enabled, the rule will be ignored for a product that has a warehouse stock entry (including "Technorama Rivilė") with a quantity greater than 0.</small> 
        </Form.Item>

        <Form.Item
          label="Active"
          name="active"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch checkedChildren="Yes" unCheckedChildren="No" />
        </Form.Item>

        <Form.Item>
          <Button
            className="w-100"
            type="primary"
            htmlType="submit"
            loading={saveMutation.isLoading}
          >
            {isEdit ? "Save" : "Add"}
          </Button>
        </Form.Item>
      </Form>
    </ModalSpecial>
  );
};

export default ModalNonPublishableKeyword;
